/**
 * 素材库业务层 — 对接 Apifox scj 素材库 Controller（文档 12）
 */

import { materialService } from '@/services/artApi';
import {
  unwrapApiList,
  normalizeRemoteCreatedId,
  unwrapApiResult,
  mapMaterialDtoToPlatform,
  mapMaterialFolderDtoToPlatform,
  mapPlatformMaterialToUploadPayload,
  normalizeLibraryFolderRows,
} from '@/utils/artMapper';
import {
  buildDepartmentOptions,
  getDepartmentRequestParams,
  getMaterialDepartmentContext,
  getMaterialDepartmentIdParam,
  isAllPlatformStore,
  resolvePlatformStorageKey,
  resolveSyncStoreIds,
  toPlatformStoreId,
  resolveShopId,
} from '@/utils/materialApiContext';
import { uploadMaterialFileToOss } from '@/services/attachmentStore';

function storeIdFromContext(storeId, user, stores = []) {
  return resolvePlatformStorageKey(storeId, user, stores);
}

function departmentParams(storeId, user, apiContext = {}) {
  const { stores, sessionDept } = buildDepartmentOptions({ storeId, user, ...apiContext });
  return getDepartmentRequestParams(storeId, user, stores, sessionDept);
}

function departmentGetParams(storeId, user, apiContext = {}) {
  const { stores, sessionDept } = buildDepartmentOptions({ storeId, user, ...apiContext });
  return getMaterialDepartmentIdParam(storeId, user, stores, sessionDept);
}

export async function fetchMaterialFolders(platform, storeId, user, parentId, apiContext = {}) {
  const { stores } = buildDepartmentOptions({ storeId, user, ...apiContext });
  const dept = departmentGetParams(storeId, user, apiContext);
  const sid = storeIdFromContext(storeId, user, stores);
  try {
    const payload = await materialService.listFolders({
      ...dept,
      parentId: parentId != null ? Number(parentId) || parentId : undefined,
    });
    if (!platform.materialFolders) platform.materialFolders = {};
    platform.materialFolders[sid] = normalizeLibraryFolderRows(
      payload,
      'materialInfo',
      'materialFolderType',
    ).map(mapMaterialFolderDtoToPlatform);
    return platform.materialFolders[sid];
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[art-api] 素材文件夹同步失败，保留本地缓存', error);
    }
    if (!platform.materialFolders) platform.materialFolders = {};
    return platform.materialFolders[sid] || [];
  }
}

export async function fetchMaterials(platform, storeId, user, query = {}, apiContext = {}) {
  const { stores } = buildDepartmentOptions({ storeId, user, ...apiContext });
  const sid = storeIdFromContext(storeId, user, stores);
  const deptParams = departmentGetParams(storeId, user, apiContext);
  let payload;
  try {
    payload = await materialService.list({
      ...deptParams,
      page: query.page || 1,
      pageSize: query.pageSize || 500,
      orderBy: query.orderBy || 'createTime',
      folderId: query.folderId != null ? Number(query.folderId) : undefined,
      materialName: query.materialName || undefined,
      materialType: query.materialType || undefined,
      onlyMine: query.onlyMine,
      filterDepartmentId: query.filterDepartmentId,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[art-api] 素材列表同步失败，保留本地缓存', error);
    }
    return {
      list: platform.materials?.filter(item => item.storeId === sid) || [],
      total: platform.materials?.filter(item => item.storeId === sid).length || 0,
    };
  }
  const remote = unwrapApiList(payload).map(dto => mapMaterialDtoToPlatform(dto, sid));
  if (isAllPlatformStore(storeId)) {
    platform.materials = remote;
  } else {
    platform.materials = platform.materials.filter(m => m.storeId !== sid).concat(remote);
  }
  return { list: remote, total: payload.totalElements ?? remote.length };
}

export async function syncMaterialLibrary(platform, storeId, user, apiContext = {}) {
  const context = { ...apiContext, storeId, user };
  const storeIds = resolveSyncStoreIds(context);
  await Promise.all(storeIds.flatMap((sid) => [
    fetchMaterialFolders(platform, sid, user, undefined, context),
    fetchMaterials(platform, sid, user, {}, context),
  ]));
}

export async function createMaterialFolderRemote(storeId, user, folderName, parentId, stores = [], apiContext = {}) {
  const dept = departmentParams(storeId, user, { stores, ...apiContext });
  const payload = await materialService.createFolder({
    ...dept,
    folderName,
    parentId: parentId != null ? Number(parentId) : 0,
  });
  return normalizeRemoteCreatedId(payload.result ?? unwrapApiResult(payload));
}

export async function renameMaterialFolderRemote(folderId, folderName) {
  await materialService.renameFolder({
    id: Number(folderId) || folderId,
    folderName,
  });
}

export async function deleteMaterialFolderRemote(folderId) {
  await materialService.deleteFolder({ id: Number(folderId) || folderId });
}

/** 拖拽素材归档/移出：调用 /api/art/material/batch-move */
export async function moveMaterialToFolderRemote(materialId, folderId) {
  const targetFolderId = folderId != null && folderId !== '' ? (Number(folderId) || folderId) : 0;
  const id = Number(materialId) || materialId;
  await batchMoveMaterialsRemote(targetFolderId, [id]);
}

/**
 * 素材上传：先直传 OSS（GET /attachment/getToken + PUT），再登记素材元数据
 */
export async function uploadMaterialRemote(storeId, user, file, meta = {}, stores = [], apiContext = {}) {
  const shopId = isAllPlatformStore(storeId) ? '0' : resolveShopId(storeId, user, stores);
  const dept = departmentParams(storeId, user, { stores, ...apiContext });
  const oss = await uploadMaterialFileToOss(file, shopId);
  const body = mapPlatformMaterialToUploadPayload({
    name: meta.name || meta.materialName,
    category: meta.category || meta.materialType,
    folderId: meta.folderId,
    url: oss.url,
    thumb: meta.thumbUrl || oss.url,
  }, {
    fileExt: meta.fileExt || oss.filename.split('.').pop() || '',
    fileSize: meta.fileSize ?? oss.fileSize,
    width: meta.width || 0,
    height: meta.height || 0,
  }, dept);
  const payload = await materialService.upload(body, dept);
  return payload.result;
}

export async function renameMaterialRemote(materialId, name) {
  await materialService.rename({
    id: Number(materialId) || materialId,
    name,
  });
}

export async function deleteMaterialRemote(materialId) {
  await materialService.delete({ id: Number(materialId) || materialId });
}

export async function batchDeleteMaterialsRemote(ids) {
  await materialService.batchDelete(ids.map(id => Number(id) || id));
}

export async function batchMoveMaterialsRemote(targetFolderId, ids) {
  await materialService.batchMove(Number(targetFolderId) || targetFolderId, ids.map(id => Number(id) || id));
}

export async function checkMaterialAccess(storeId, user, materialId, apiContext = {}) {
  const dept = departmentGetParams(storeId, user, apiContext);
  await materialService.checkAccess({
    materialId: Number(materialId) || materialId,
    ...dept,
  });
}
